import React from 'react';
import { Form, Select, InputNumber, Modal } from 'antd';
import { useGetFspsByType } from "@/modules/admin/fsps/hooks/fsp";
import { useGetPaymentProcess } from "@/modules/admin/fsps/hooks/use-payment-process";
import {FspType,SelfOnboardingConfigDTO} from '@/shared/api/model';
import { formatAmount } from "@/components/common/utils/money";

const { Option } = Select;

interface BankPaymentHandlerProps {
    onBankSelect: (bank: string) => void;
    onAmountChange: (amount: string) => void;
    config: SelfOnboardingConfigDTO;
    dark: boolean;
}

const BankPaymentHandler: React.FC<BankPaymentHandlerProps> = ({
                                                                   onBankSelect,
                                                                   onAmountChange,
                                                                   config,
                                                                   dark
                                                               }) => {
    const [{ data: banks, isLoading: isLoadingBanks }] = useGetFspsByType(FspType.BANK);
    const [selectedBank, setSelectedBank] = React.useState<string>('');
    const [{ data: paymentProcess }] = useGetPaymentProcess(FspType.BANK, selectedBank, 'en');
    const [showProcessModal, setShowProcessModal] = React.useState(false);

    const handleBankChange = (bankCode: string) => {
        setSelectedBank(bankCode);
        onBankSelect(bankCode);

        // Show payment process modal when bank is selected
        if (bankCode && paymentProcess?.processDefinition) {
            setShowProcessModal(true);
        }
    };

    const handleAmountChange = (value: number | null) => {
        if (value) {
            onAmountChange(value.toString());
        }
    };

    const processDefinition = paymentProcess?.processDefinition ?
        JSON.parse(paymentProcess.processDefinition) : null;

    return (
        <>
            <Form.Item name="bankCode" label="Select Bank" rules={[{ required: true }]}>
                <Select
                    showSearch
                    loading={isLoadingBanks}
                    onChange={handleBankChange}
                    placeholder="Select Bank"
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                        // @ts-ignore
                        (option?.children as string).toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                >
                    {banks?.map((bank) => (
                        <Option key={bank.code} value={bank.code}>
                            {bank.name || bank.institutionName}
                        </Option>
                    ))}
                </Select>
            </Form.Item>

            <Form.Item name="amount" label="Initial Deposit" rules={[
                { required: true },
                {
                    validator: (_, value) => {
                        // @ts-ignore
                        if (value < config?.minDepositAmount) {
                            return Promise.reject(`Amount must be at least ${formatAmount(Number(config.minDepositAmount))} TZS`);
                        }
                        // @ts-ignore
                        if (value > config?.maxDepositAmount) {
                            return Promise.reject(`Amount cannot exceed ${formatAmount(Number(config.maxDepositAmount))} TZS`);
                        }
                        return Promise.resolve();
                    }
                }
            ]}>
                <InputNumber
                    style={{ width: '100%' }}
                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    // @ts-ignore
                    parser={(value) => value!.replace(/TZS\s?|(,*)/g, "")}
                    onChange={handleAmountChange}
                    className={dark ? 'dark-mode-input' : ''}
                    min={config.minDepositAmount}
                    max={config.maxDepositAmount}
                />
            </Form.Item>

            <Modal
                title="Bank Payment Instructions"
                open={showProcessModal}
                onOk={() => setShowProcessModal(false)}
                onCancel={() => setShowProcessModal(false)}
                width={700}
            >
                {processDefinition && (
                    <div className="space-y-4">
                        {processDefinition.steps.map((step: any, index: number) => (
                            <div key={index} className="mb-4 pb-4 border-b">
                                <h3 className="font-bold">{`${index + 1}. ${step.instruction}`}</h3>
                                <p className="text-gray-600">{step.description}</p>
                            </div>
                        ))}
                        {processDefinition.additionalInfo && (
                            <div className="mt-4 p-4 bg-gray-100 rounded">
                                <h4 className="font-bold">Additional Information</h4>
                                <p>{processDefinition.additionalInfo.supportContact}</p>
                                <p>{processDefinition.additionalInfo.notes}</p>
                            </div>
                        )}
                    </div>
                )}
            </Modal>
        </>
    );
};

export default BankPaymentHandler;